import type { OperationRequest } from '../api/types';

// Builders for the ADR-025 wire operations. Every operation carries a fresh UUID so the
// server-authoritative sequencer can treat redelivery as a no-op; component ids are
// client-assigned UUIDs so blocks keep their identity across moves and edits. The id source
// is injected (ADR-029 §2 — `src/platform/ids.ts` on every install) so the builders stay
// free of any global the host may lack.

/** What the builders need from the host: fresh ids. */
export interface OperationDeps {
  readonly newId: () => string;
}

export interface OperationBuilders {
  /** Adds a block under `parentId` (omitted = top level), appended at the end. */
  makeAdd(type: string, title: string, parentId?: string): OperationRequest;
  /** Sets (or, with null, removes) one property on a block — per-key last writer wins. */
  makeEdit(componentId: string, key: string, value: string | null): OperationRequest;
  /** Moves a block within its parent (parentId omitted = top level) to `index`. */
  makeMove(componentId: string, index: number, parentId?: string): OperationRequest;
  /** Removes a block and its whole subtree. */
  makeRemove(componentId: string): OperationRequest;
}

/** The operation builders bound to an id source. */
export function createOperations({ newId }: OperationDeps): OperationBuilders {
  return {
    makeAdd(type, title, parentId) {
      return {
        operationId: newId(),
        kind: 'add',
        componentId: newId(),
        ...(parentId === undefined ? {} : { parentId }),
        type,
        properties: { title },
      };
    },

    makeEdit(componentId, key, value) {
      return { operationId: newId(), kind: 'edit', componentId, properties: { [key]: value } };
    },

    makeMove(componentId, index, parentId) {
      return {
        operationId: newId(),
        kind: 'move',
        componentId,
        ...(parentId === undefined ? {} : { parentId }),
        index,
      };
    },

    makeRemove(componentId) {
      return { operationId: newId(), kind: 'remove', componentId };
    },
  };
}
